import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { pipe, ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { createGuid } from '../../../core/helpers/utility';
import { ICampaignAudit, INotes } from '../../../core/models/campaigns.model';
import { CampaignsApiService } from '../campaigns.apiservice';
import { AppService } from '../../../store/app.service';
import { ICampaign } from '../../../core/models/campaigns.model';
import { DataFeedService } from '../../../shared/api/datafeed.service';

@Component({
  selector: 'app-campaign-audit',
  templateUrl: './campaign-audit.component.html',
  styleUrls: ['./campaign-audit.component.scss']
})
export class CampaignAuditComponent implements OnInit {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  campaignData: ICampaign = {} as ICampaign;
  platform = '';

  bitOptions = [
    {
      title: 'YES',
      value: true
    },
    {
      title: 'NO',
      value: false
    },
    {
      title: 'NA',
      value: null
    }
  ]
  submitted = false;
  userId: string = '';
  auditData: Array<ICampaignAudit>;
  auditForm: FormGroup;
  campaignId: string = '';
  @Input() iCanWrite = false;
  @Input() set iCampaignId(id: string) {
    this.campaignId = id;
    this.campaignService.getAudit(id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        if (response.data) {
          const auditDataNulls: any = {};
          for (const key in response.data) {
            if (response.data[key] === null) {
              auditDataNulls[key] = null;
            }
          }
          this.auditForm.patchValue({ 
            id: response.data.id || null,
            campaignId: id,
            ...auditDataNulls 
          });
        } else {
          this.auditForm.patchValue({ campaignId: id });
        }
      });
  }

  constructor(
    private appService: AppService,
    private dataFeedService: DataFeedService,
    private campaignService: CampaignsApiService,
    private formBuilder: FormBuilder,
  ) {
    this.auditData = new Array<ICampaignAudit>();
    this.auditForm = this.formBuilder.group({
      id: [null],
      // lastCheck: [null],
      campaignId: [null],
      adsLive: [''],
      producingLeads: [''],
      leadsEmailChecked: [''],
      adsSalesMatch: [''],
      adsSetRefreshed: [''],
      locationTargetCorrect: [''],
      budgetMatchSubscription: [''],
      fbDemographicCorrect: [''],
      // fbLeadsCenterAccess: [''],
      googleAdsCorrectLandingPage: [''],
      googleKeywordsCorrect: [''],
      googleNegtiveKeywords: [''],
      lpLandingPageCorrect: [''],
      lpPageTimeLong: [''],
      lpCrossPlatform: [''],
      lpCorrectOfferShown: [''],
      // communicationWithClient: [''],
      cpL30d: ['', Validators.required],
      cpL7d: ['', Validators.required],
      lastClientCallDate: [null, Validators.required],
      lastEmailSentDate: [null, Validators.required],
      // lastAudit: [null],
      internalNotes: [''],
      externalNotes: ['']
    });
  }

  ngOnInit(): void {
    this.appService.getActiveUser$()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(u => {
        if (u.id !== undefined) {
          this.userId = u.id;
        }
      });
    this.loadCampaignDetails(this.campaignId);
  }
  
  loadCampaignDetails(id: string): void {
    this.campaignService.getCampaignDetails(id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        this.campaignData = response.data;
        // console.log('Campaign Data:', this.campaignData);
        this.platform = this.campaignData.platform;
      });
  }

  onAuditSubmit() {
    this.submitted = true;
    if (this.auditForm.invalid) {
      return;
    }
    if (this.f.id.value === null) {
      this.f.id.setValue(createGuid());
    }
      
    let formValues: ICampaignAudit = this.auditForm.value;
    this.campaignService.saveAudit(formValues)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        this.createAuditNote(formValues);
      })
  }

  get f() { return this.auditForm.controls; }

  createAuditNote(values: any) {
    const formatDate = (dateStr: string) => {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    };
  
    let body = [
      `CPL (Last 30 Days): $${values.cpL30d}`,
      `CPL (Last 7 Days): $${values.cpL7d}`,
      `Last Call: ${formatDate(values.lastClientCallDate)}`,
      `Last Email: ${formatDate(values.lastEmailSentDate)}`,
      ``
    ];
    
    const skipKeys = ['id', 'campaignId', 'cpL30d', 'cpL7d', 'lastClientCallDate', 'lastEmailSentDate', 'internalNotes', 'externalNotes'];
    if (this.campaignData.platform === 'Google') {
      skipKeys.push('fbDemographicCorrect', 'adsSetRefreshed');
    } else if (this.campaignData.platform === 'Facebook') {
      skipKeys.push('googleAdsCorrectLandingPage', 'googleKeywordsCorrect', 'googleNegtiveKeywords');
    }
    const textValues: any = {};

    for (const key in values) {
      if (!skipKeys.includes(key)) {
        textValues[key] = values[key] === true ? 'Yes' : values[key] === false ? 'No' : 'N/A';
      }
    }

    const headings: { [key: string]: string } = {
      adsLive: 'Are the Campaign and Ads Live and Delivering?',
      producingLeads: 'Is the Campaign Producing Leads?',
      leadsEmailChecked: 'Leads Checked in REACH to Confirm Lead Delivery? (Non-Xpo Only)',
      adsSalesMatch: 'Are all Naming Conventions correct on the Campaign, AdSet/AdGroup, and Ad level? Are all Copy and Creatives Approved and Correct?',
      locationTargetCorrect: 'Is the Location Targeting Correct?',
      budgetMatchSubscription: 'Does the Campaign Daily Budget Match the Subscription?',
      fbDemographicCorrect: 'Is the Demographic Targeting Correct?',
      adsSetRefreshed: 'Have the Ads Been Refreshed Recently?',
      googleAdsCorrectLandingPage: 'Are the Ads Directing to the Correct Landing Page?',
      googleKeywordsCorrect: 'Are the Correct Keywords Being Used?',
      googleNegtiveKeywords: 'Are the Negative Keywords / Keyword Lists Being Used?',
      lpLandingPageCorrect: 'Does the Landing Page Load Correctly?',
      lpPageTimeLong: 'Is the Landing Page Load Time Excessively Long?',
      lpCrossPlatform: 'Does the Landing Page Look Right on Mobile and Desktop?',
      lpCorrectOfferShown: 'Is the Correct Offer Being Shown on the Landing Page?'
    };
    
    for (const key in textValues) {
      body.push(`${headings[key as keyof typeof headings] || key.replace(/([A-Z])/g, ' $1').trim()}: ${textValues[key]}`);
    }

    if (values.internalNotes > '') {
      body.push(``);
      body.push('Audit Notes');
      body.push(`${values.internalNotes}`);
    }

    let bodytext = body.join('\n');
    
    let date = new Date();
    let note: INotes = {
      campaignId: values.campaignId,
      id: createGuid(),
      updatedOn: date,
      assignedUserName: '',
      assignedUserId: this.userId,
      title: 'Campaign Audit',
      description: bodytext,
      type: 1,
      clientId: '',
      campaignName: values.campaignName,
      isInternal: true,
      noteFiles: []
    }
    this.dataFeedService.saveNote(note)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        // this.resetAuditForm();
      });

    let externalBody = [
      'The monthly audit for this campaign has been completed. All key items and fundamental components have been thoroughly reviewed, checked, and verified as correct.',
      ``,
      `CPL (Last 30 Days): $${values.cpL30d}`,
      `CPL (Last 7 Days): $${values.cpL7d}`
    ];
    if (values.externalNotes > '') {
      externalBody.push(``);
      externalBody.push('DMS Notes');
      externalBody.push(`${values.externalNotes}`);
    }

    let externalBodytext = externalBody.join('\n');

    let externalNote: INotes = {
      campaignId: values.campaignId,
      id: createGuid(),
      updatedOn: date,
      assignedUserName: '',
      assignedUserId: this.userId,
      title: 'Full Campaign Audit Completed',
      description: externalBodytext,
      type: 1,
      clientId: '',
      campaignName: values.campaignName,
      isInternal: false,
      noteFiles: []
    }

    this.dataFeedService.saveNote(externalNote)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
      });
  }
/*
  resetAuditForm() {
    this.auditForm = this.formBuilder.group({
      id: [null],
      // lastCheck: [null],
      campaignId: [null],
      adsLive: [''],
      producingLeads: [''],
      leadsEmailChecked: [''],
      adsSalesMatch: [''],
      adsSetRefreshed: [''],
      locationTargetCorrect: [''],
      budgetMatchSubscription: [''],
      fbDemographicCorrect: [''],
      // fbLeadsCenterAccess: ['],
      googleAdsCorrectLandingPage: [''],
      googleKeywordsCorrect: [''],
      googleNegtiveKeywords: [''],
      lpLandingPageCorrect: [''],
      lpPageTimeLong: [''],
      lpCrossPlatform: [''],
      lpCorrectOfferShown: [''],
      // communicationWithClient: ['],
      cpL30d: ['', Validators.required],
      cpL7d: ['', Validators.required],
      lastClientCallDate: [null, Validators.required],
      lastEmailSentDate: [null, Validators.required],
      // lastAudit: [null],
      internalNotes: [''],
      externalNotes: ['']
    });

    this.campaignService.getAudit(this.campaignId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((response: any) => {
        const auditDataNulls: any = {};
        for (const key in response.data) {
          if (response.data[key] === null) {
            auditDataNulls[key] = null;
          }
        }
        this.auditForm.patchValue({ 
          id: response.data.id,
          campaignId: this.campaignId,
          ...auditDataNulls 
        });
      });
  }
*/
}
